import React, { useState, useEffect } from 'react';
import { getProductColorsDynamic, testColorData } from '../utils/colorDataDynamic';

const ColorSelectionModal = ({ product, onClose, onAddToCart }) => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [show, setShow] = useState(false); // Para animación
  const [colors, setColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(true);

  useEffect(() => {
    console.log('🎨 ColorSelectionModal useEffect ejecutado');
    console.log('📱 Producto:', product);
    console.log('📝 Nombre del producto:', product.name);
    
    setShow(true);
    setLoadingColors(true);
    
    // Primero hacer una prueba de los datos
    testColorData().then(() => {
      // Usar la función que ya consulta Google Sheets en tiempo real (detección automática)
      getProductColorsDynamic(product.name.replace(/ DS$/, ''))
        .then(res => {
          console.log('✅ Colores obtenidos:', res);
          setColors(res);
        })
        .catch((error) => {
          console.error('❌ Error obteniendo colores:', error);
          setColors([]);
        })
        .finally(() => setLoadingColors(false));
    });
  }, [product.name, product.category]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      onClose();
    }, 200); // Debe coincidir con la duración de la transición
  };

  // Agrupar por color y mostrar el stock más favorable
  const stockPriority = { 'Stock': 3, 'Bajo Stock': 2, 'Bajo stock': 2, 'Sin Stock': 1 };
  const uniqueColors = Object.values(
    colors.reduce((acc, color) => {
      const key = color.name.trim().toLowerCase();
      if (!acc[key] || stockPriority[color.stock] > stockPriority[acc[key].stock]) {
        acc[key] = color;
      }
      return acc;
    }, {})
  );
  // Solo seleccionables los que tienen Stock o Bajo Stock
  const availableColors = uniqueColors.filter(color => color.stock === 'Stock' || color.stock === 'Bajo stock' || color.stock === 'Bajo Stock');

  const handleAddToCart = () => {
    if (selectedColor) {
      onAddToCart({
        ...product,
        selectedColor: selectedColor.name,
        quantity: selectedQuantity
      });
      onClose();
    }
  };

  // Determinar cantidad máxima según el stock
  const getMaxQuantity = (stock) => {
    if (stock === 'Bajo Stock' || stock === 'Bajo stock') {
      return 2;
    } else if (stock === 'Stock') {
      return 10;
    }
    return 1; // Para "Sin Stock" o cualquier otro caso
  };

  const maxQuantity = selectedColor ? getMaxQuantity(selectedColor.stock) : 1;

  const getStockStatusColor = (stock) => {
    switch (stock) {
      case "Stock":
        return "text-green-600 bg-green-100";
      case "Bajo Stock":
      case "Bajo stock":
        return "text-yellow-600 bg-yellow-100";
      case "Sin Stock":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 transition-opacity duration-200 animate-fade-in">
      {console.log('🎨 ColorSelectionModal renderizando con show:', show)}
      <div
        className={`bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all duration-200 ${
          show ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Seleccionar Color</h2>
            <button 
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4">
            <h3 className="font-medium text-gray-900 mb-2">{product.name.replace(/ DS$/, '')}</h3>
            <div className="flex flex-col gap-1">
              <p className="text-2xl font-bold text-gray-900">
                ${product.pricears?.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
              <p className="text-lg font-semibold text-green-600">
                U$D {product.price?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Colores Disponibles</h4>
            {loadingColors ? (
              <div className="text-gray-500 text-center py-4">Cargando colores...</div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              {uniqueColors.map((color, index) => (
                <button
                  key={index}
                  onClick={() => (color.stock === 'Stock' || color.stock === 'Bajo stock' || color.stock === 'Bajo Stock') && setSelectedColor(color)}
                  disabled={!(color.stock === 'Stock' || color.stock === 'Bajo stock' || color.stock === 'Bajo Stock')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedColor?.name === color.name
                      ? 'border-black bg-gray-50'
                      : (color.stock === 'Stock' || color.stock === 'Bajo stock' || color.stock === 'Bajo Stock')
                      ? 'border-gray-200 hover:border-gray-300'
                      : 'border-red-200 bg-red-50 text-red-600 opacity-80 cursor-not-allowed'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{color.name}</div>
                    <div className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${getStockStatusColor(color.stock)}`}>
                      {color.stock}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            )}
          </div>

          {/* Advertencia para Bajo Stock */}
          {selectedColor && (selectedColor.stock === "Bajo Stock" || selectedColor.stock === "Bajo stock") && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-yellow-800 text-sm font-medium">
                   Stock limitado - Apúrate antes de que se agote!
                </p>
              </div>
            </div>
          )}

          {availableColors.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Cantidad</h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 sm:space-x-3">
                  <button
                    onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-lg font-medium w-12 text-center">{selectedQuantity}</span>
                  <button
                    onClick={() => setSelectedQuantity(Math.min(maxQuantity, selectedQuantity + 1))}
                    disabled={selectedQuantity >= maxQuantity}
                    className={`w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center ${
                      selectedQuantity >= maxQuantity 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedColor || availableColors.length === 0}
                  className={`py-2 sm:py-3 px-4 rounded-lg text-sm font-medium transition-all duration-150 ${
                    selectedColor && availableColors.length > 0
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:transform active:scale-95 active:shadow-inner'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  style={selectedColor && availableColors.length > 0 ? { boxShadow: 'inset 2px 2px 8px #bbb, inset -2px -2px 8px #fff' } : {}}
                >
                  Agregar al Carrito
                </button>
              </div>
            </div>
          )}

          {/* Si NO hay colores disponibles, permitir agregar igual */}
          {availableColors.length === 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex flex-col items-center">
              <p className="text-red-700 text-sm mb-3">
                No hay colores disponibles en stock para este producto.
              </p>
              <button
                onClick={() => {
                  onAddToCart({ ...product, quantity: selectedQuantity });
                  onClose();
                }}
                className="py-2 sm:py-3 px-4 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-base font-medium transition-all duration-150 active:transform active:scale-95 active:shadow-inner"
                style={{ boxShadow: 'inset 2px 2px 8px #bbb, inset -2px -2px 8px #fff' }}
              >
                Agregar al Carrito igual
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorSelectionModal; 